import {
  CopyIcon,
  ExternalLinkIcon,
  MaximizeIcon,
  PlayCircleIcon,
  VideoIcon,
  XIcon,
} from 'lucide-react';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
} from 'react';

import type { CaseStepDetail } from '@/lib/api/quality-types.js';
import { fetchArtifactBlob } from '@/lib/api/quality.js';
import { Button } from '@/components/ui/button.js';
import { notify } from '@/lib/notify.js';
import { cn } from '@/lib/utils.js';

import { AuthImage } from '../AuthMedia.js';
import {
  activeChapterIndex,
  buildChapters,
  chapterSeekMs,
  type Chapter,
} from './chapters.js';
import { ChapterList } from './ChapterList.js';
import { PLAYER_DOCK_ID } from './player-dock-id.js';
import type { ClipWindow, Recording } from './recordings.js';

/** A pending seek: jump the bound recording, keyed by `key`, to `ms`. `nonce` makes repeat clicks
 *  on the same timecode re-fire the seek even though `key`/`ms` are unchanged. */
export interface PlayerDockSeekRequest {
  key: string;
  ms: number;
  nonce: number;
}

/**
 * An explicit "load this recording now" command from the caller — a timecode click, a chapter
 * click, a selector click. The dock never infers a load from a mere seek or bind: a deep-link
 * restore also binds and seeks (arming the player at a target timecode, AC 27) but must NOT
 * download anything until the user's own gesture, so the caller only sends this alongside a real
 * one. `nonce` lets a repeat click on an already-loaded recording still register (mirrors
 * `PlayerDockSeekRequest`).
 */
export interface PlayerDockLoadRequest {
  key: string;
  nonce: number;
}

/**
 * How big the frame gets, by the two things that actually decide it: the recording's own proportions
 * and where the player currently lives.
 *
 * Orientation is not a detail — most recordings are LANDSCAPE (a desktop web run at 1920x1080, a
 * horizontal iPad), and only BrowserStack phone sessions are portrait. A landscape clip wants width
 * and a portrait one wants height, so a single sizing rule always starves one of them: sizing by
 * height (the first attempt at this) left a 16:9 clip 360x202 in a side column, and sizing by width
 * paints the black void around a 9:19.5 clip that this whole iteration exists to remove.
 *
 * `inline` is the Video tab, where the player owns a wide column: a landscape clip fills it (~880px
 * at 1920) and a portrait one is bounded by height instead. `modal` is the floating, NON-blocking panel a
 * timecode on the Cases tab opens in the bottom-right corner: a quick look, so its height is about
 * half the screen for landscape (50vh -> ~960x540 on a 1080p screen) and taller for portrait, which
 * has nowhere else to grow.
 */
function mediaBoxStyle(
  aspectRatio: number,
  variant: PlayerDockVariant,
): CSSProperties {
  const heightBudget = variant === 'modal' ? '58vh' : '78vh';
  const widthCap = variant === 'modal' ? '60rem' : '100%';
  return {
    aspectRatio,
    /** Bounded on BOTH axes in one expression, because bounding one and deriving the other always
     *  breaks somewhere: a fixed height derives a width that a tall screen pushes past the card's
     *  own cap, and the frame then letterboxes inside its black box — the exact defect this sizing
     *  exists to remove. Whichever limit binds first wins, and the aspect ratio holds either way. */
    width: `min(${widthCap}, 88vw, calc(${heightBudget} * ${aspectRatio}))`,
    height: 'auto',
  };
}

/** Where the single player currently sits: the overlay opened from a case's timecode, or the Video
 *  tab's own wide column. A class swap on one stable subtree — never a second element, never a
 *  re-parent. */
export type PlayerDockVariant = 'modal' | 'inline';

export { PLAYER_DOCK_ID } from './player-dock-id.js';

/**
 * Starting aspect ratio, before anything about the real recording is known — a GUESS, and always
 * LANDSCAPE regardless of kind. Most recordings ARE landscape: a desktop web run records 1920x1080,
 * and BrowserStack iPad sessions run horizontally too; a phone held in portrait is the minority case.
 * A guess should be wrong for the minority, not the majority. The guess rarely has to stand for long:
 * a mobile recording's `src` is now set (with `preload="metadata"`) as soon as it's bound, so
 * `loadedmetadata` corrects it before any play (see `onVideoLoadedMetadata`); the poster corrects it
 * sooner still when the bound case has one (`onPosterNaturalSize`).
 */
function defaultAspectRatio(): number {
  return 16 / 9;
}

/** Copies a BrowserStack session id with the repo's toast idiom (see `DeckDetail.tsx`). */
async function copySessionId(sessionId: string): Promise<void> {
  try {
    await navigator.clipboard.writeText(sessionId);
    notify.success('Session id copied.');
  } catch {
    notify.error('Clipboard unavailable');
  }
}

/**
 * Bearer-fetched object URLs for the dock's bound WEB recordings, bounded to the two MOST RECENTLY
 * USED artifact URLs (a real 2-entry LRU — a cache hit re-touches its entry's recency exactly like a
 * fresh fetch does, see `load` below) so an A→B→A rebind sequence — comparing two cases of one run —
 * reuses both blobs instead of re-downloading either, and A staying the more recently used of the two
 * survives a third distinct load (evicting B, the actually-stale one) instead of being evicted itself
 * mid-playback. A third distinct URL evicts the least recently used entry, revoking its object URL.
 * Deliberately private to the dock: `AuthMedia.tsx`'s shared `blobCache` excludes video entirely (S4)
 * because its cap counts entries, not bytes.
 *
 * Keyed on the artifact URL itself (which embeds the run id, see `artifactUrl` in `lib/api/quality.ts`)
 * rather than the recording's bare `key` (a run-RELATIVE S3 key, e.g. `case1/video.webm`) — two
 * different runs can reuse the same relative key, and keying on it made a rebind across runs read as
 * "already loaded", playing run A's footage under run B's case (S2). `onEvict` lets the caller (the
 * dock's own `loadedKeys`) drop its own bookkeeping for whatever URL this cache just evicted, so
 * "loaded but no object URL" can't arise (S2/S9). `onError` lets the caller un-mark a failed URL as
 * loaded, so a 404/network error doesn't dead-end the dock on "Loading recording…" forever (S9).
 */
function useWebRecordingObjectUrls(callbacks: {
  onEvict: (evictedUrls: string[]) => void;
  onError: (artifactUrl: string) => void;
}): {
  objectUrlFor: (artifactUrl: string) => string | undefined;
  load: (artifactUrl: string) => void;
} {
  const [objectUrls, setObjectUrls] = useState<Map<string, string>>(new Map());
  /** Mirrors `objectUrls` synchronously (state updates are not visible until the next render) so
   *  `load` can read the CURRENT map — including an eviction it just computed — without waiting for
   *  a commit. Written only here, right alongside `setObjectUrls`, so there is exactly one place
   *  that can make the two drift. */
  const objectUrlsRef = useRef(objectUrls);
  const recencyOrder = useRef<string[]>([]);
  const inFlightUrls = useRef<Set<string>>(new Set());
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
      for (const objectUrl of objectUrlsRef.current.values())
        URL.revokeObjectURL(objectUrl);
    },
    [],
  );

  const load = (artifactUrl: string): void => {
    if (objectUrlsRef.current.has(artifactUrl)) {
      /** A cache HIT — e.g. the A → B → A leg of a rebind sequence, reusing A's already-fetched
       *  blob — still counts as "just used A" and must push it back to the tail of the recency
       *  order here, exactly like a fresh fetch does below. Without this, a hit left the reused
       *  entry stranded wherever its ORIGINAL fetch had put it, so the next distinct load (C) could
       *  evict it via the ordinary FIFO shift below even though it was the most recently used entry
       *  — and possibly the one currently bound and playing (Copilot round 4). Callers re-invoke
       *  `load` on every bind, including a rebind to an already-cached url (`PlayerDock`'s
       *  `triggerLoad`, called from every fresh `loadRequest`), so this hit path runs on every such
       *  rebind and keeps the bound recording out of eviction's reach as long as it stays bound. */
      recencyOrder.current = [
        ...recencyOrder.current.filter((existing) => existing !== artifactUrl),
        artifactUrl,
      ];
      return;
    }
    if (inFlightUrls.current.has(artifactUrl)) return;
    inFlightUrls.current.add(artifactUrl);
    fetchArtifactBlob(artifactUrl)
      .then((blob) => {
        if (!mounted.current) return;
        const objectUrl = URL.createObjectURL(blob);
        /** Compute the next map and every eviction OUTSIDE the `setState` updater: React's Strict
         *  Mode invokes an updater function twice to detect impurity, and mutating `recencyOrder`
         *  or calling `URL.revokeObjectURL` from inside one would run those side effects twice too. */
        recencyOrder.current = [
          ...recencyOrder.current.filter(
            (existing) => existing !== artifactUrl,
          ),
          artifactUrl,
        ];
        const next = new Map(objectUrlsRef.current);
        next.set(artifactUrl, objectUrl);
        const evictedUrls: string[] = [];
        const evictedObjectUrls: string[] = [];
        while (next.size > 2) {
          const evictedUrl = recencyOrder.current.shift();
          if (evictedUrl === undefined) break;
          const evictedObjectUrl = next.get(evictedUrl);
          if (evictedObjectUrl) evictedObjectUrls.push(evictedObjectUrl);
          evictedUrls.push(evictedUrl);
          next.delete(evictedUrl);
        }
        objectUrlsRef.current = next;
        setObjectUrls(next);
        for (const evictedObjectUrl of evictedObjectUrls)
          URL.revokeObjectURL(evictedObjectUrl);
        if (evictedUrls.length > 0) callbacks.onEvict(evictedUrls);
      })
      .catch(() => {
        if (!mounted.current) return;
        notify.error('Recording unavailable');
        callbacks.onError(artifactUrl);
      })
      .finally(() => {
        inFlightUrls.current.delete(artifactUrl);
      });
  };

  return { objectUrlFor: (artifactUrl) => objectUrls.get(artifactUrl), load };
}

/**
 * The single player for a Quality run's report page. Rendered exactly once, from one JSX site in
 * `QualityRunDetail` — a sibling of the tab PANELS inside the always-mounted `<Tabs>` root, never
 * inside a `TabsContent` (Radix unmounts an inactive panel, or hides it, and a hidden subtree has
 * zero intersection, so a bearer-fetched player there would never load).
 *
 * It has two placements, and both are class swaps on this one stable subtree — never a second
 * element, never a re-parent, so a switch costs no remount, no re-download and no lost playhead:
 *
 * - `modal` (every tab but Video): a floating, NON-blocking panel in the bottom-right corner. No
 *   backdrop and nothing inert behind it — the case's error text stays readable and clickable while
 *   the recording plays — shown only while `open`, with a "Video tab" button as the way out to the
 *   big player.
 * - `inline` (the Video tab): the tab's own wide column, in flow. `open` does not apply there; the
 *   player IS the tab's content. The page supplies that column's grid `order` and sticky offset
 *   through `className` — where the element sits on the page is the page's business, and everything
 *   about how it looks is this component's.
 *
 * Renders `null` once no recording is bound, so a fresh page mounts zero `<video>` elements.
 *
 * A WEB recording's `src` is withheld until the user has explicitly loaded it (the in-dock "Load
 * recording" button, or a caller's explicit load command) — it is a bearer-fetched S3 blob, and
 * nothing about it can be known before that fetch runs. A MOBILE recording's `src` is a direct,
 * already-authorized BrowserStack URL, so it is set as soon as the recording is bound, with
 * `preload="metadata"`: the browser issues a small range request for just the header, which gives
 * the frame its real shape (`onVideoLoadedMetadata`) before any play, without downloading the clip
 * itself — there is exactly one `<video>` element on the whole page, so this costs nothing like the
 * multi-megabyte, multi-player downloads the "no fetch before a gesture" rule exists to prevent. The
 * overlay (below) still gates actual PLAYBACK behind the user's own "Play recording" click for both
 * kinds — nothing here autoplays. A pending recording still binds and shows its own explicit
 * placeholder rather than a silent no-op.
 */
export function PlayerDock({
  recording,
  variant = 'modal',
  className,
  open,
  onOpenChange,
  onOpenVideoTab,
  posterUrl = null,
  seek,
  loadRequest,
  caseSteps,
  clipWindow,
  chapterScopeCaseId = null,
  chapterScopeAttemptNumber = null,
}: {
  recording: Recording | null;
  variant?: PlayerDockVariant;
  /** Placement classes from the page (the Video tab's grid column and sticky offset). The dock owns
   *  everything about its own look; the page only says where its one element sits. */
  className?: string;
  /** Governs the `modal` variant only. The `inline` player is the Video tab's own content — there is
   *  nothing to close there, and closing it would leave the tab empty. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Switches to the Video tab with the current recording still bound: the overlay is for a quick
   *  look, and this is the way out to the big player without re-selecting anything (or re-fetching —
   *  it is the same element and the same `src`). Omitted when the run has no Video tab. */
  onOpenVideoTab?: () => void;
  /** A screenshot from the bound case, shown behind the unloaded frame. The old unloaded state was a
   *  plain black rectangle, which reads as a broken element rather than as "press play". */
  posterUrl?: string | null;
  seek: PlayerDockSeekRequest | null;
  loadRequest?: PlayerDockLoadRequest | null;
  /** Steps of the case the dock is currently bound to (S9) — never the whole run/session. Omitted
   *  or empty renders no chapter list. */
  caseSteps?: CaseStepDetail[];
  /** The bound case's own window inside the recording (mobile only); null for a web recording or
   *  when the window isn't known yet (Branch B — chapters fall back to the full timeline). */
  clipWindow?: ClipWindow | null;
  /** Identity of the case (and, for a re-queued case, the attempt) `caseSteps` is scoped to — used
   *  ONLY to decide when to reset chapter state (see the reset effect below), never to build the
   *  chapters themselves. `caseSteps` itself is not a stable reference to key that reset on: a live
   *  run's report bundle refetches and replaces it with a new, deeply-equal array even when the
   *  bound case hasn't changed, which reset-on-`caseSteps` read as a rebind and silently dropped an
   *  armed chapter seek (Copilot round 4). Omitted while no case is bound. */
  chapterScopeCaseId?: string | null;
  chapterScopeAttemptNumber?: number | null;
}): JSX.Element | null {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const [liveAnnouncement, setLiveAnnouncement] = useState('');
  /** The recording whose REAL dimensions `loadedmetadata` has reported. Cleared on every rebind, so a
   *  poster is always allowed to shape a recording nothing has measured yet. */
  const metadataAppliedFor = useRef<string | null>(null);
  const handledSeekNonce = useRef<number | null>(null);
  const handledLoadNonce = useRef<number | null>(null);
  const handledChapterSeekNonce = useRef<number | null>(null);
  const chapterSeekNonce = useRef(0);
  /** Cache keys (an artifact URL for web, the session `src` for mobile — see `cacheKeyFor` below),
   *  never the recording's bare `key`: a run-relative S3 key can repeat across runs (S2), and this
   *  set must never read "already loaded" for a recording nothing has actually fetched. */
  const [loadedKeys, setLoadedKeys] = useState<Set<string>>(new Set());
  /** Cache keys whose load attempt failed — drives the 'error' overlay + retry (S9). Cleared the
   *  moment a new load attempt starts for that key. */
  const [erroredKeys, setErroredKeys] = useState<Set<string>>(new Set());
  const [aspectRatio, setAspectRatio] = useState<number>(defaultAspectRatio);
  /** A chapter click's pending seek (S9/AC 20) — mirrors the `seek` PROP's own request/nonce/effect
   *  shape below, kept separate because it's driven by an internal click, not a caller prop. */
  const [chapterSeekRequest, setChapterSeekRequest] = useState<{
    ms: number;
    nonce: number;
  } | null>(null);

  const pruneLoadedKeys = (evictedKeys: string[]): void => {
    setLoadedKeys((previous) => {
      if (evictedKeys.every((key) => !previous.has(key))) return previous;
      const next = new Set(previous);
      for (const key of evictedKeys) next.delete(key);
      return next;
    });
  };
  const markKeyErrored = (key: string): void => {
    setLoadedKeys((previous) => {
      if (!previous.has(key)) return previous;
      const next = new Set(previous);
      next.delete(key);
      return next;
    });
    setErroredKeys((previous) => new Set(previous).add(key));
  };
  const { objectUrlFor, load } = useWebRecordingObjectUrls({
    onEvict: pruneLoadedKeys,
    onError: markKeyErrored,
  });

  const chapters = useMemo<Chapter[]>(
    () => buildChapters(caseSteps ?? [], clipWindow ?? null),
    [caseSteps, clipWindow],
  );
  const chapterTimecodesMs = useMemo(
    () => chapters.map((chapter) => chapter.timecodeMs),
    [chapters],
  );
  const [activeChapterIdx, setActiveChapterIdx] = useState(-1);

  /** A rebind to a different recording, OR a rebind to a different case WITHIN the same recording,
   *  starts chapters over — no chapter is "active" until the next `timeupdate` tick resolves one
   *  against the newly bound scope, and any chapter seek still armed for the scope just left is
   *  dropped so it can never misapply once THIS scope (or whatever binds next) reaches
   *  `loadedmetadata` (S9). `recording?.key` alone is not enough: a shared BrowserStack session
   *  covers several cases (the mobile N:1 shape), so switching which case is bound leaves the
   *  session's `key` unchanged while the bound case — what the chapters are actually scoped to —
   *  switches underneath it.
   *
   *  Depends on `chapterScopeCaseId`/`chapterScopeAttemptNumber` — stable SCALARS the caller derives
   *  from its own `boundCaseId`/`boundAttemptNumber` state — rather than on `caseSteps` itself
   *  (round 3's choice) or on `clipWindow` (a freshly allocated object every render regardless of
   *  whether the window actually changed, so keying on its identity would reset the chapters on
   *  every unrelated re-render). `caseSteps` looked stable enough under react-query's structural
   *  sharing, but that only holds while the data stays deeply equal — a live run's report bundle
   *  refetches and replaces the array with a new, deeply-equal reference even when the bound case
   *  hasn't changed, which reset-on-`caseSteps` misread as a rebind and silently dropped a chapter
   *  seek armed while the video was still loading (Copilot round 4). A scalar case/attempt id can't
   *  suffer that: it is either unchanged (no reset, even across a refetch) or changed (a real rebind,
   *  including the mobile N:1 case-switch above, still resets). */
  useEffect(() => {
    setActiveChapterIdx(-1);
    setChapterSeekRequest(null);
  }, [recording?.key, chapterScopeCaseId, chapterScopeAttemptNumber]);

  /** AC 19: recomputed on every `timeupdate` (~4x/s) via a cheap binary search, but `setState` only
   *  fires when the resolved index actually changed — a naive version would re-render the whole
   *  chapter list on every tick. */
  const onVideoTimeUpdate = (): void => {
    const video = videoRef.current;
    if (!video || chapterTimecodesMs.length === 0) return;
    const nextIndex = activeChapterIndex(
      chapterTimecodesMs,
      video.currentTime * 1000,
    );
    setActiveChapterIdx((previousIndex) =>
      previousIndex === nextIndex ? previousIndex : nextIndex,
    );
  };

  const cacheKeyFor = (rec: Recording): string => rec.src ?? rec.key;

  /** Loads `rec` the same way the "Load recording" button does — shared by that button, a chapter
   *  click on an unloaded recording (S9), and any future gesture that needs to trigger a fetch. */
  const triggerLoad = (rec: Recording): void => {
    const key = cacheKeyFor(rec);
    setErroredKeys((previous) => {
      if (!previous.has(key)) return previous;
      const next = new Set(previous);
      next.delete(key);
      return next;
    });
    setLoadedKeys((previous) => new Set(previous).add(key));
    if (rec.kind === 'web' && rec.src) load(rec.src);
  };

  const cacheKey = recording ? cacheKeyFor(recording) : null;
  const loaded =
    recording !== null && cacheKey !== null && loadedKeys.has(cacheKey);
  const errored = cacheKey !== null && erroredKeys.has(cacheKey);
  /** A mobile recording's `src` is a direct BrowserStack URL, not a bearer-fetched blob — there is
   *  no fetch step to gate behind `loaded`, so it is available the moment the recording is bound
   *  (and not pending). This is what lets `preload="metadata"` below give the frame its real shape
   *  before any play. */
  const mobileMetadataSrc =
    recording && recording.kind === 'mobile' && !recording.pending
      ? (recording.src ?? undefined)
      : undefined;
  const src = !recording
    ? undefined
    : recording.kind === 'mobile'
      ? mobileMetadataSrc
      : loaded
        ? objectUrlFor(recording.src ?? '')
        : undefined;

  /** AC 20: a chapter click seeks the same way as the `seek` prop's effect below — applied right
   *  away once the element can accept it, deferred to `loadedmetadata` otherwise (see the
   *  `chapterSeekRequest` effect). A chapter outside the current clip window (stale after a rebind)
   *  does not seek. Unlike the seek prop, this can fire before the recording is loaded at all — the
   *  common case for the entry state (AC 20 must work with nothing yet fetched) — so it also
   *  requests a load rather than parking a raw DOM listener with no `src` to ever resolve it. */
  const onChapterSelect = (chapter: Chapter): void => {
    if (!recording) return;
    const targetMs = chapterSeekMs(chapter, clipWindow ?? null);
    if (targetMs === null) return;
    chapterSeekNonce.current += 1;
    setChapterSeekRequest({ ms: targetMs, nonce: chapterSeekNonce.current });
    if (!loaded && !recording.pending) triggerLoad(recording);
  };

  /** A rebind to a DIFFERENT key resets the aspect ratio to that recording's default; `loadedmetadata`
   *  below replaces it with the real one. Rebinding to the SAME key (AC 11) leaves this alone. */
  useEffect(() => {
    if (!recording) return;
    metadataAppliedFor.current = null;
    setAspectRatio(defaultAspectRatio());
  }, [recording?.key]);

  /** Applies a pending seek once the element can accept it. Same idiom as `VideoReport`'s (soon-dead)
   *  seek effect, minus the scroll: this dock never scrolls the page (AC 8). Validates `seek.key`
   *  against the CURRENTLY bound recording — a seek left over from a bind the caller failed to clear
   *  (or one addressed to a recording since replaced) must never apply to whatever is bound now.
   *  Gated on `loaded`, not just `src`: a mobile recording's `src` is set as soon as it's bound (for
   *  the metadata-only preload), but a deep-link restore arms a `seek` with no `loadRequest` — the
   *  user hasn't loaded anything yet (S11, AC 27) — and setting `currentTime` moves the play
   *  position, which can pull real media bytes rather than just the header this dock allows before a
   *  gesture. A web recording's `src` already implied `loaded` (it's a bearer-fetched blob that only
   *  exists once loaded), so this changes nothing there. */
  useEffect(() => {
    const video = videoRef.current;
    if (
      !seek ||
      !video ||
      !src ||
      !loaded ||
      !recording ||
      seek.key !== recording.key ||
      handledSeekNonce.current === seek.nonce
    ) {
      return undefined;
    }
    const applySeek = (): void => {
      handledSeekNonce.current = seek.nonce;
      video.pause();
      try {
        video.currentTime = seek.ms / 1000;
      } catch {
        /** not seekable yet */
      }
    };
    if (video.readyState >= 1) {
      applySeek();
      return undefined;
    }
    video.addEventListener('loadedmetadata', applySeek, { once: true });
    return () => video.removeEventListener('loadedmetadata', applySeek);
  }, [seek, src, loaded]);

  /** Applies a chapter click's pending seek (S9) once the element can accept it — same idiom as the
   *  `seek` prop's effect above. Keyed on `recording?.key` too (not just `chapterSeekRequest`/`src`)
   *  so a rebind ALWAYS tears down a still-pending `loadedmetadata` listener before it can fire
   *  against whatever loads next; the `chapterSeekRequest` reset above already nulls the request on
   *  the same rebind, so this mostly documents the invariant rather than doing independent work.
   *  Gated on `loaded`, not just `src`, for the same reason as the `seek` prop effect above: a
   *  chapter click on a still-PENDING mobile session arms the seek WITHOUT calling `triggerLoad`
   *  (`onChapterSelect`'s own guard skips it while `recording.pending`), so when the session turns
   *  ready and `src` appears, the seek must still wait for the user's own gesture rather than
   *  applying itself via `loadedmetadata`. */
  useEffect(() => {
    const video = videoRef.current;
    if (
      !chapterSeekRequest ||
      !video ||
      !src ||
      !loaded ||
      handledChapterSeekNonce.current === chapterSeekRequest.nonce
    ) {
      return undefined;
    }
    const applyChapterSeek = (): void => {
      handledChapterSeekNonce.current = chapterSeekRequest.nonce;
      video.pause();
      try {
        video.currentTime = chapterSeekRequest.ms / 1000;
      } catch {
        /** not seekable yet */
      }
    };
    if (video.readyState >= 1) {
      applyChapterSeek();
      return undefined;
    }
    video.addEventListener('loadedmetadata', applyChapterSeek, { once: true });
    return () => video.removeEventListener('loadedmetadata', applyChapterSeek);
  }, [chapterSeekRequest, src, loaded, recording?.key]);

  /** Applies an explicit load command from the caller (S7/S8's one-click gesture) — the same effect
   *  as clicking "Load recording" below, just triggered without the extra click. Guarded by `key` so
   *  a stale command left over from a since-rebound recording never fires. A pending recording (AC
   *  12) defers acting on the request — marking it loaded now, before there is anything to fetch,
   *  would leave it stuck once it turns ready, since nothing else re-triggers the fetch — so the
   *  effect keeps re-checking on every render until `pending` clears, THEN marks the nonce handled
   *  (mirrors the seek effect's `handledSeekNonce`) so it only fires once per gesture. */
  useEffect(() => {
    if (!loadRequest || !recording || loadRequest.key !== recording.key) return;
    if (recording.pending) return;
    if (handledLoadNonce.current === loadRequest.nonce) return;
    handledLoadNonce.current = loadRequest.nonce;
    triggerLoad(recording);
  }, [loadRequest, recording, load]);

  /** The dock never moves focus on its own. It used to pull focus into the card whenever it opened,
   *  which — because the effect also ran on a mode change — threw focus out of the tab list every
   *  time the user arrow-keyed back to Cases (WCAG 3.2.1 On Focus). Binding a recording is a side
   *  effect of clicking a timecode, not a navigation, so the caret stays on the chip the user
   *  clicked and the change is announced instead. */
  useEffect(() => {
    if (!recording) return;
    /** Names the CASE, not just the recording: one BrowserStack session can cover several cases, so
     *  selecting a different case leaves `recording.label` (and its key) unchanged while the thing
     *  the player is actually scoped to changes underneath. Without the case, an AT user hears the
     *  same sentence twice and learns nothing. */
    const scope = chapterScopeCaseId ? ` — case #${chapterScopeCaseId}` : '';
    setLiveAnnouncement(`Recording bound: ${recording.label}${scope}`);
  }, [recording?.key, recording?.label, chapterScopeCaseId]);

  /** The panel never takes focus. It is non-blocking by design — the user clicked a timecode while
   *  reading a case, and moving the caret into a floating player would take them out of the text they
   *  are reading (and, when the effect also ran on a placement change, out of the tab list entirely —
   *  WCAG 3.2.1). The bind is announced through the live region above instead. */
  if (!recording) return null;

  /** Shared by the in-dock "Load recording"/"Retry" button and `loadRequest` above. */
  const onLoadClick = (): void => triggerLoad(recording);

  /** The rail is the READING size; full screen is the inspecting size. Native `controls` already
   *  carry a fullscreen button, but it is a 16px glyph inside the frame and nobody finds it — this
   *  puts the same capability in the dock header, where the size complaint is actually felt. */
  const onExpandClick = (): void => {
    void videoRef.current?.requestFullscreen?.();
  };

  /** The poster is a screenshot of the same device, so its proportions are the recording's. It lands
   *  before any play, which is exactly when the guessed default would otherwise stand — a landscape
   *  iPad session shaped as a tall phone until someone pressed play. `loadedmetadata` still wins
   *  afterwards; this only replaces a guess with a measurement. */
  const onPosterNaturalSize = (size: {
    width: number;
    height: number;
  }): void => {
    /** Keyed on the recording, not on `videoRef.current.videoWidth`: a `<video>` keeps the previous
     *  clip's dimensions after a rebind to an unloaded one, so a width check would read as "metadata
     *  already known" and leave the new recording wearing the guessed default forever. */
    if (metadataAppliedFor.current === recording?.key) return;
    setAspectRatio(size.width / size.height);
  };

  const onVideoLoadedMetadata = (): void => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) return;
    metadataAppliedFor.current = recording.key;
    setAspectRatio(video.videoWidth / video.videoHeight);
  };

  /** 'pending': nothing to load yet. 'error': the last load attempt failed — retry available. 'load':
   *  ready, awaiting the user's gesture. 'loading': gesture given, the web blob fetch hasn't resolved
   *  yet. `null`: playable — no overlay. Pending takes priority over a stale error from a PREVIOUS
   *  recording that happened to share this one's cache key (can't really happen — keys are run+
   *  artifact-scoped — but pending is the more honest state regardless). */
  const overlayState: 'pending' | 'error' | 'load' | 'loading' | null =
    recording.pending
      ? 'pending'
      : errored
        ? 'error'
        : !loaded
          ? 'load'
          : !src
            ? 'loading'
            : null;

  /** A closed session that brought back no recording is not "still recording": promising one that
   *  will never arrive is the copy that had clients waiting on a video the run never produced. */
  const pendingMessage = recording.unavailable
    ? 'No recording was saved for this test group — it will not appear later.'
    : recording.kind === 'mobile'
      ? 'Video is still recording — it will appear once this test group finishes.'
      : 'Video is still processing — it will appear once this case finishes.';

  const modal = variant === 'modal';
  /** The poster is an artifact fetch, so it obeys the same "nothing before a gesture" rule as the
   *  recording itself: a deep link that ARMS a recording leaves the panel closed, and a closed panel
   *  must not pull a screenshot in the background (a hidden subtree still mounts and still fetches).
   *  Opening the panel, or switching to the Video tab, is that gesture. */
  /** The URL itself, not a boolean: narrowing through `Boolean(posterUrl)` leaves `posterUrl` typed
   *  `string | null` at the render site, and `tsc -b` rejects it there. */
  const posterSrc =
    posterUrl && overlayState !== 'pending' && (!modal || open)
      ? posterUrl
      : null;

  return (
    <>
      <div
        ref={cardRef}
        id={PLAYER_DOCK_ID}
        role="region"
        tabIndex={modal ? -1 : undefined}
        aria-label={`Recording: ${recording.label}`}
        onKeyDown={(event) => {
          /** Escape closes the panel only while focus is inside it — a document-level listener also
           *  swallowed the Escape meant for the command palette. Fullscreen is the other claimant: the
           *  `<video>` this handler sits above is exactly what `requestFullscreen` targets, so leaving
           *  fullscreen with Escape would otherwise dismiss the panel underneath it in the same
           *  keystroke. */
          if (event.key !== 'Escape' || !modal) return;
          if (document.fullscreenElement) return;
          onOpenChange(false);
        }}
        className={cn(
          'flex flex-col gap-2 rounded-lg border border-border bg-card p-3 outline-none',
          modal
            ? /** A floating panel in the bottom-right corner — deliberately NON-blocking: no backdrop,
               *  nothing inert behind it, so the case's error text stays readable and clickable while
               *  the recording plays. `w-auto` lets the card hug whatever shape the frame resolved to;
               *  the size, not the corner, was the original complaint. */
              'fixed bottom-4 right-4 z-40 max-h-[80vh] w-auto max-w-[min(64rem,94vw)] overflow-y-auto shadow-2xl'
            : 'static w-full',
          modal && !open && 'hidden',
          className,
        )}
      >
        <p aria-live="polite" className="sr-only">
          {liveAnnouncement}
        </p>
        <div className="flex items-center gap-1.5 text-sm">
          <VideoIcon
            className="size-4 shrink-0 text-muted-foreground"
            aria-hidden
          />
          <span
            className="min-w-0 flex-1 truncate font-medium"
            title={recording.label}
          >
            {recording.label}
          </span>
          {/* Same gate as `controls` (`loaded && Boolean(src)`), not `Boolean(src)` alone: a mobile
            recording's `src` is set before any load gesture, and `requestFullscreen` targets only
            the `<video>` — a fullscreen paused, control-less video with the Play overlay left
            behind in the un-fullscreened page is a dead end reachable only by pressing Escape. */}
          {loaded && src ? (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={onExpandClick}
              aria-label="Expand recording to full screen"
              title="Full screen"
            >
              <MaximizeIcon />
            </Button>
          ) : null}
          {/* The session id is support-desk material, not identity: it used to occupy the whole header
            and truncate the case label to nothing, and its accessible name was 40 hex characters. */}
          {recording.kind === 'mobile' && recording.sessionId ? (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => void copySessionId(recording.sessionId!)}
              aria-label="Copy BrowserStack session id"
              title={`Copy BrowserStack session id (${recording.sessionId})`}
            >
              <CopyIcon />
            </Button>
          ) : null}
          {/* The overlay is a quick look; this is the way out to the Video tab's big player, with the
            recording still bound and already loaded (same element, same `src`, no re-fetch). */}
          {modal && onOpenVideoTab ? (
            <Button
              variant="outline"
              size="sm"
              onClick={onOpenVideoTab}
              className="shrink-0"
            >
              <ExternalLinkIcon aria-hidden /> Video tab
            </Button>
          ) : null}
          {modal ? (
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => onOpenChange(false)}
              aria-label="Close recording"
            >
              <XIcon />
            </Button>
          ) : null}
        </div>

        <div className="flex min-h-0 flex-col gap-2">
          {/* The box takes the VIDEO's shape, never the column's — see `mediaBoxStyle`. `bg-black` then
          covers the frame and nothing else, instead of painting a void around it. */}
          <div
            style={mediaBoxStyle(aspectRatio, variant)}
            className="relative mx-auto flex max-w-full shrink-0 items-center justify-center overflow-hidden rounded-md bg-black"
          >
            <video
              ref={videoRef}
              src={src}
              /** Requires BOTH `loaded` and `src`, not `Boolean(src)` alone (the original condition) and
               *  not `loaded` alone: a mobile recording's `src` is set as soon as it's bound (metadata
               *  only), so `Boolean(src)` alone would show keyboard-focusable native controls before the
               *  user ever clicked the Play overlay — a Tab + Space could start real playback through
               *  them. A web recording's `src` is a bearer-fetched blob that resolves ASYNCHRONOUSLY
               *  after `loaded` flips true (the `overlayState === 'loading'` gap), so `loaded` alone would
               *  show controls with nothing yet playable. Only once both are true is there something to
               *  control and a gesture that already happened. */
              controls={loaded && Boolean(src)}
              preload={recording.kind === 'mobile' ? 'metadata' : 'none'}
              aria-label={`Recording of ${recording.label}`}
              className="size-full object-contain"
              onLoadedMetadata={onVideoLoadedMetadata}
              onTimeUpdate={onVideoTimeUpdate}
              // eslint-disable-next-line jsx-a11y/media-has-caption -- test recordings have no captions
            />
            {overlayState ? (
              <div role="status" className="absolute inset-0">
                {/* A frame from the case behind the call to action: an unloaded recording used to be a
                plain black rectangle, which reads as a broken element rather than as "press play".
                The screenshot is an artifact the report already carries, so this costs no new
                download the cases table hasn't made anyway. */}
                {posterSrc ? (
                  <AuthImage
                    src={posterSrc}
                    alt=""
                    className="absolute inset-0 size-full object-cover opacity-40"
                    onNaturalSize={onPosterNaturalSize}
                  />
                ) : null}
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 p-4 text-center text-xs text-white/90">
                  {overlayState === 'pending' ? (
                    pendingMessage
                  ) : overlayState === 'loading' ? (
                    'Loading recording… a long session can take a minute.'
                  ) : (
                    <button
                      type="button"
                      onClick={onLoadClick}
                      className="flex items-center gap-2 rounded-full bg-white/95 px-4 py-2 text-sm font-medium text-black shadow-lg transition-transform hover:scale-105"
                    >
                      <PlayCircleIcon className="size-5" aria-hidden />
                      {overlayState === 'error'
                        ? 'Retry loading'
                        : 'Play recording'}
                    </button>
                  )}
                </div>
              </div>
            ) : null}
          </div>

          <ChapterList
            chapters={chapters}
            activeIndex={activeChapterIdx}
            onSelect={onChapterSelect}
            chapterScopeCaseId={chapterScopeCaseId}
            chapterScopeAttemptNumber={chapterScopeAttemptNumber}
          />
        </div>
      </div>
    </>
  );
}
